import React, { useCallback, useMemo, useRef, useState } from 'react';

import type { ApolloLink } from '@apollo/client';
import { ButtonBase, IconButton, Typography } from '@mui/material';
import clsx from 'clsx';
import { Plus, SquarePen } from 'lucide-react';
import { omit, uniq, uniqBy } from 'ramda';
import { EditableNameInput } from 'src/components/EditableNameInput';
import { UploadFilesPicker } from 'src/components/FilestackPicker';
import { AlineTooltip, SmallCloseIconButton } from 'src/components/generic';
import { NoDocumentsIcon } from 'src/components/icons/NoDocumentsIcon';
import { ReplaceWithThirdPartyIcon } from 'src/components/icons/ReplaceWithThirdPartyIcon';
import { UploadIcon } from 'src/components/icons/UploadIcon';
import { Apollo } from 'src/gql/generated';
import { FileStackDomContainer } from 'src/helpers';
import { useGetDocumentsSimple } from 'src/hooks';
import { makeStyles } from 'tss-react/mui';

import { getDocumentNameUpdate } from './launch-document-name';
import type { OnValuesChange, TemplateQuickStartState } from './LaunchTemplateBody';
import { StepLayout } from '../StepLayout';

type RestoreOriginal = (documentId: UUID) => void;
type LaunchTemplateFiles = NonNullable<Apollo.LaunchTemplateDataQuery['workflow']>['files'];

export function AddDocuments({
  onValuesChange,
  quickStartState,
  restoreOriginal,
  templateWorkflowFiles,
}: {
  onValuesChange: OnValuesChange;
  quickStartState: TemplateQuickStartState;
  restoreOriginal: RestoreOriginal;
  templateWorkflowFiles: LaunchTemplateFiles;
}) {
  return (
    <AddDocumentList
      quickStartState={quickStartState}
      restoreOriginal={restoreOriginal}
      templateWorkflowFiles={templateWorkflowFiles}
      onValuesChange={onValuesChange}
    />
  );
}

const useAddDocumentsListStyles = makeStyles()(theme => ({
  addButton: {
    color: theme.appColors.iconsType,
  },
  documentList: {
    padding: theme.spacing(2, 0),
  },
  main: {
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(2),
  },
}));

type PickerState =
  | {
      action: 'replace';
      pickerOpen: boolean;
      templateId: UUID;
    }
  | {
      action: 'upload';
      pickerOpen: boolean;
    };

export function AddDocumentList({
  onValuesChange,
  quickStartState,
  restoreOriginal,
  templateWorkflowFiles,
}: {
  onValuesChange: OnValuesChange;
  quickStartState: TemplateQuickStartState;
  restoreOriginal: RestoreOriginal;
  templateWorkflowFiles: LaunchTemplateFiles;
}) {
  const { classes } = useAddDocumentsListStyles();
  const [pickerState, setPickerState] = useState<PickerState>({
    action: 'upload',
    pickerOpen: false,
  });
  const [replaceWithThirdParty, setReplaceWithThirdParty] = useState<Record<UUID, string>>({});

  const afterUploadDone = useCallback(
    async (res: ApolloLink.Result<Apollo.CreateFilesMutation>) => {
      const documents = res.data?.createFiles ?? [];
      if (!documents.length) return;

      switch (pickerState.action) {
        case 'replace': {
          const document = documents.at(0);
          if (!document) return;

          onValuesChange({
            replacementFiles: uniqBy(
              f => f.templateId,
              [
                ...quickStartState.replacementFiles,
                { replacementFileId: document.id, templateId: pickerState.templateId },
              ]
            ),
          });
          setReplaceWithThirdParty({
            ...replaceWithThirdParty,
            [pickerState.templateId]: document.name,
          });
          break;
        }
        case 'upload': {
          const newDocumentIds = documents.map(doc => doc.id);
          onValuesChange({
            moreDocumentIds: uniq([...quickStartState.moreDocumentIds, ...newDocumentIds]),
          });
          break;
        }
      }

      setPickerState(prevState => ({ ...prevState, pickerOpen: false }));
    },
    [onValuesChange, pickerState, quickStartState, replaceWithThirdParty]
  );

  return (
    <StepLayout
      divider
      action={
        <AlineTooltip arrow placement="top" title="Add document">
          <IconButton
            aria-label="Add document"
            className={classes.addButton}
            size="small"
            onClick={() => setPickerState({ action: 'upload', pickerOpen: true })}
          >
            <Plus size={16} />
          </IconButton>
        </AlineTooltip>
      }
      title="Documents"
    >
      <div className={classes.main}>
        <AddDocumentListBody
          openPicker={templateId => {
            setPickerState({ action: 'replace', pickerOpen: true, templateId });
          }}
          quickStartState={quickStartState}
          removeNewDocument={documentId => {
            onValuesChange({
              moreDocumentIds: quickStartState.moreDocumentIds.filter(id => id !== documentId),
            });
          }}
          replaceWithThirdParty={replaceWithThirdParty}
          restoreOriginal={documentId => {
            setReplaceWithThirdParty(prev => omit([documentId], prev));
            restoreOriginal(documentId);
          }}
          templateWorkflowFiles={templateWorkflowFiles}
          onValuesChange={onValuesChange}
        />
      </div>
      <UploadFilesPicker
        domContainer={FileStackDomContainer.LaunchTemplate}
        // Third-party paper must arrive as a new document to take the template document's slot.
        offerUploadAsVersion={pickerState.action !== 'replace'}
        open={pickerState.pickerOpen}
        pickerOpts={{
          afterUploadDone,
          maxFiles: pickerState.action === 'replace' ? 1 : undefined,
          onClose: () => setPickerState(prev => ({ ...prev, pickerOpen: false })),
          props: {
            documentVariant: Apollo.DocumentVariant.Document,
            isSigned: false,
          },
        }}
      />
    </StepLayout>
  );
}

export function AddDocumentListBody({
  onValuesChange,
  openPicker,
  quickStartState,
  removeNewDocument,
  replaceWithThirdParty,
  restoreOriginal,
  templateWorkflowFiles,
}: {
  onValuesChange: OnValuesChange;
  openPicker: (templateId: UUID) => void;
  quickStartState: TemplateQuickStartState;
  removeNewDocument: (documentId: UUID) => void;
  replaceWithThirdParty: Record<UUID, string>;
  restoreOriginal: (documentId: UUID) => void;
  templateWorkflowFiles: LaunchTemplateFiles;
}) {
  const { classes } = useAddDocumentsListStyles();
  const { documents: moreDocuments } = useGetDocumentsSimple(quickStartState.moreDocumentIds);
  const allDocuments = useMemo(
    () => [
      ...(templateWorkflowFiles?.map(workflowFile => workflowFile.document) ?? []),
      ...moreDocuments,
    ],
    [templateWorkflowFiles, moreDocuments]
  );

  if (!allDocuments.length) return <NoDocuments />;

  return (
    <div className={classes.documentList}>
      {allDocuments.map(document => {
        const isDisabled = quickStartState.excludedDocumentIds.includes(document.id);
        const isNewFile = quickStartState.moreDocumentIds.some(id => id === document.id);
        return (
          <DocumentRow
            key={document.id}
            document={document}
            isDisabled={isDisabled}
            isNewFile={isNewFile}
            isReplacedWithThirdParty={quickStartState.replacementFiles.some(
              f => f.templateId === document.id
            )}
            openPicker={openPicker}
            quickStartState={quickStartState}
            removeNewDocument={removeNewDocument}
            replacementDocumentTitle={
              document.id in replaceWithThirdParty ? replaceWithThirdParty[document.id] : null
            }
            restoreOriginal={restoreOriginal}
            onValuesChange={onValuesChange}
          />
        );
      })}
    </div>
  );
}

const useDocumentRowStyles = makeStyles()(theme => ({
  documentActions: {
    alignItems: 'center',
    display: 'flex',
    flexDirection: 'row',
    // Keep Restore / Replace / close buttons fully visible when the name is long.
    flexShrink: 0,
    gap: theme.spacing(1),
  },
  documentNameContainer: {
    padding: 0,
  },
  documentNameEditButton: {
    padding: theme.spacing(0.5),
  },
  documentNameInputBase: {
    fontSize: 14,
    fontWeight: 600,
    // Let the autosize input shrink below its content width so ellipsis can apply.
    minWidth: 0,
  },
  documentNameWithEdit: {
    alignItems: 'center',
    display: 'flex',
    // Take leftover row space and shrink before actions; basis 0 so long names don't force overflow.
    flex: '1 1 0',
    flexDirection: 'row',
    gap: theme.spacing(0.5),
    // Flex items default to min-width:auto, which blocks shrinking past content size.
    minWidth: 0,
    overflow: 'hidden',
  },
  documentNameWithEditDisabled: {
    opacity: 0.5,
  },
  documentRow: {
    alignItems: 'center',
    display: 'flex',
    flexDirection: 'row',
    gap: theme.spacing(1),
    height: 48,
    justifyContent: 'space-between',
    minWidth: 0,
    overflow: 'hidden',
    padding: theme.spacing(1, 0),
    width: '100%',
  },
  documentRowDisabled: {
    alignItems: 'center',
    display: 'flex',
    flexDirection: 'row',
    gap: theme.spacing(1),
    height: 48,
    justifyContent: 'space-between',
    minWidth: 0,
    overflow: 'hidden',
    padding: theme.spacing(1, 0),
    width: '100%',
  },
}));

function DocumentNameSlot({
  document,
  isDisabled,
  isNewFile,
  onValuesChange,
  quickStartState,
  replacementDocumentTitle,
}: {
  document: Pick<Apollo.Document, 'id' | 'name'>;
  isDisabled: boolean;
  isNewFile: boolean;
  onValuesChange: OnValuesChange;
  quickStartState: TemplateQuickStartState;
  replacementDocumentTitle: string | null;
}) {
  const [updateDocumentProps] = Apollo.useUpdateDocumentPropsMutation();
  const { classes } = useDocumentRowStyles();
  const nameInputRef = useRef<HTMLInputElement>(null);

  if (typeof replacementDocumentTitle === 'string') {
    return <ReplacedWithThirdParty replacementDocumentTitle={replacementDocumentTitle} />;
  }

  const currentName =
    quickStartState.templateIdToContractName[document.id] ?? document.name ?? 'Untitled';

  function handleNameUpdate(name: string) {
    const update = getDocumentNameUpdate({
      documentId: document.id,
      isNewFile,
      name,
      templateIdToContractName: quickStartState.templateIdToContractName,
    });

    if (update.action === 'updateLaunchName') {
      onValuesChange({ templateIdToContractName: update.templateIdToContractName });
      return;
    }

    void updateDocumentProps({
      variables: {
        input: { documentId: update.documentId, documentProps: { name: update.name } },
      },
    });
  }

  function handleEditClick() {
    nameInputRef.current?.focus();
    nameInputRef.current?.select();
  }

  return (
    <>
      <EditableNameInput
        ref={nameInputRef}
        disabled={isDisabled}
        fullWidth={false}
        inputBaseClassName={classes.documentNameInputBase}
        name={currentName}
        updateName={handleNameUpdate}
        wrapperClassName={classes.documentNameContainer}
      />
      <AlineTooltip arrow placement="top" title="Edit document name">
        <IconButton
          aria-label="Edit document name"
          className={classes.documentNameEditButton}
          disabled={isDisabled}
          size="small"
          onClick={handleEditClick}
        >
          <SquarePen size={16} />
        </IconButton>
      </AlineTooltip>
    </>
  );
}

function DocumentRow({
  document,
  isDisabled,
  isNewFile,
  isReplacedWithThirdParty,
  onValuesChange,
  openPicker,
  quickStartState,
  removeNewDocument,
  replacementDocumentTitle,
  restoreOriginal,
}: {
  document: Pick<Apollo.Document, 'id' | 'name'>;
  isDisabled: boolean;
  isNewFile: boolean;
  isReplacedWithThirdParty: boolean;
  onValuesChange: OnValuesChange;
  openPicker: (templateId: UUID) => void;
  quickStartState: TemplateQuickStartState;
  removeNewDocument: (documentId: UUID) => void;
  replacementDocumentTitle: string | null;
  restoreOriginal: (documentId: UUID) => void;
}) {
  const { classes } = useDocumentRowStyles();

  return (
    <div className={isDisabled ? classes.documentRowDisabled : classes.documentRow}>
      <div
        className={clsx(
          classes.documentNameWithEdit,
          isDisabled && classes.documentNameWithEditDisabled
        )}
      >
        <DocumentNameSlot
          document={document}
          isDisabled={isDisabled}
          isNewFile={isNewFile}
          quickStartState={quickStartState}
          replacementDocumentTitle={replacementDocumentTitle}
          onValuesChange={onValuesChange}
        />
      </div>
      <div className={classes.documentActions}>
        {!isDisabled && (
          <>
            <ReplaceWithThirdParty
              documentId={document.id}
              isNew={isNewFile}
              isReplacedWithThirdParty={isReplacedWithThirdParty}
              openPicker={openPicker}
              restoreOriginal={() => restoreOriginal(document.id)}
            />
            <AlineTooltip arrow placement="top" title="Remove document from workflow">
              <SmallCloseIconButton
                onClick={() => {
                  if (isNewFile) removeNewDocument(document.id);
                  else
                    onValuesChange({
                      excludedDocumentIds: [...quickStartState.excludedDocumentIds, document.id],
                    });
                }}
              />
            </AlineTooltip>
          </>
        )}
        {isDisabled && (
          <AlineTooltip arrow placement="top" title="Restore document">
            <SmallCloseIconButton
              variant="restore"
              onClick={() => {
                onValuesChange({
                  excludedDocumentIds: quickStartState.excludedDocumentIds.filter(
                    id => id !== document.id
                  ),
                });
              }}
            />
          </AlineTooltip>
        )}
      </div>
    </div>
  );
}

const useReplaceWithThirdPartyStyles = makeStyles()(theme => ({
  button: {
    '&:hover': {
      backgroundColor: theme.appColors.hoverBackground,
    },
    alignItems: 'center',
    border: `1px solid ${theme.appColors.rule}`,
    borderRadius: 100,
    boxSizing: 'border-box',
    display: 'flex',
    flexShrink: 0,
    gap: theme.spacing(0.5),
    height: 32,
    padding: theme.spacing(1, 1.5),
  },
  buttonText: {
    whiteSpace: 'nowrap',
  },
}));

interface ReplaceWithThirdPartyProps {
  documentId: UUID;
  isNew: boolean;
  isReplacedWithThirdParty: boolean;
  openPicker: (templateId: UUID) => void;
  restoreOriginal: () => void;
}

function ReplaceWithThirdParty({
  documentId,
  isNew,
  isReplacedWithThirdParty,
  openPicker,
  restoreOriginal,
}: ReplaceWithThirdPartyProps): JSX.Element | null {
  const { classes } = useReplaceWithThirdPartyStyles();

  if (isNew) return null;

  if (isReplacedWithThirdParty)
    return (
      <ButtonBase className={classes.button} onClick={restoreOriginal}>
        <Typography className={classes.buttonText} variant="subtitle2">
          Restore original
        </Typography>
      </ButtonBase>
    );

  return (
    <>
      <ButtonBase className={classes.button} onClick={() => openPicker(documentId)}>
        <UploadIcon />
        <Typography className={classes.buttonText} variant="subtitle2">
          Replace with third-party paper
        </Typography>
      </ButtonBase>
    </>
  );
}

const useReplacedWithThirdPartyStyles = makeStyles()(theme => ({
  icon: {
    // Don't squash the replace icon when the filename truncates.
    flexShrink: 0,
    height: 20,
    width: 20,
  },
  iconWrapper: {
    alignItems: 'center',
    display: 'flex',
    flexShrink: 0,
    justifyContent: 'center',
  },
  replacedWithThirdParty: {
    alignItems: 'center',
    display: 'flex',
    flexDirection: 'row',
    gap: theme.spacing(1),
    minWidth: 0,
    overflow: 'hidden',
    width: '100%',
  },
  title: {
    // Fill space after the icon and shrink so long filenames ellipsis instead of overflowing.
    flex: '1 1 0',
    minWidth: 0,
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
  },
}));

function ReplacedWithThirdParty({
  replacementDocumentTitle,
}: {
  replacementDocumentTitle: string | null;
}) {
  const { classes } = useReplacedWithThirdPartyStyles();

  if (typeof replacementDocumentTitle !== 'string') return null;

  const title = replacementDocumentTitle || 'Untitled';
  const label = `Replaced with "${title}"`;

  return (
    <AlineTooltip arrow placement="top" title={title}>
      <div className={classes.replacedWithThirdParty}>
        <div className={classes.iconWrapper}>
          <ReplaceWithThirdPartyIcon className={classes.icon} />
        </div>
        <Typography className={classes.title} variant="subtitle2">
          {label}
        </Typography>
      </div>
    </AlineTooltip>
  );
}

const useNoDocumentsStyles = makeStyles()(theme => ({
  main: {
    alignItems: 'center',
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(2),
    justifyContent: 'center',
  },
}));

function NoDocuments() {
  const { classes } = useNoDocumentsStyles();

  return (
    <div className={classes.main}>
      <NoDocumentsIcon />
      <Typography variant="subtitle2">No documents. Click upload to add.</Typography>
    </div>
  );
}
